<template>
  <ModalContainer
    class="create-view-modal"
    :title="t('dataGrid.views.createModal.title')"
    icon="AddCircleOutline"
    :open="open"
    :primary-button="primaryButtonArgs"
    :cancel-button-text="t('common.cancel')"
    :data-qa="dataGridViewsDataQa.createModalTitle"
    :data-qa-cancel-button="dataGridViewsDataQa.cancelBtn"
    :data-qa-primary-button="dataGridViewsDataQa.createNewViewBtn"
    :data-qa-close="dataGridViewsDataQa.closeIcon"
    @on-close="handleClose"
    @primary-button-click="handleSave"
  >
    <div class="create-view-modal-content">
      <AlertItem theme="info" :title="t('dataGrid.views.createModal.infoBannerTitle')">
        <div class="create-view-modal-info body-regular-default">
          <ul class="create-view-modal-info-list">
            <li>{{ t('dataGrid.views.createModal.infoBannerBullet') }}</li>
          </ul>
          <p>{{ t('dataGrid.views.createModal.infoBannerFooter') }}</p>
        </div>
      </AlertItem>
      <TextInput
        v-model="name"
        :label="t('dataGrid.views.modal.nameLabel')"
        :validator="validateName"
        :error-text="nameErrorText"
        :data-qa="dataGridViewsDataQa.nameInput"
        :data-qa-label="dataGridViewsDataQa.nameLabel"
        @update:model-value="onNameInput"
      />
      <CheckboxItem
        v-model:checked="setAsDefault"
        :text="t('dataGrid.views.modal.setAsDefault')"
        value="setAsDefault"
        :disabled="true"
        :required="false"
        :data-qa="dataGridViewsDataQa.setDefaultCheckbox"
      />
    </div>
  </ModalContainer>
</template>

<script setup lang="ts">
import { toRef } from 'vue';
import { AlertItem, CheckboxItem, ModalContainer, TextInput } from '@lw-design-system/ui';
import { useViewNameValidation } from '@/composables/common/data-grid/useViewNameValidation';
import { dataGridViewsDataQa } from '~/constants/commons/data-qa';

const { t } = useI18n();

const props = defineProps<{ open: boolean; serverDuplicateError: boolean }>();
const emit = defineEmits<{
  (e: 'on-close'): void;
  (e: 'save', name: string): void;
  (e: 'clear-server-duplicate-error'): void;
}>();

const { name, setAsDefault, trimmedName, validateName, nameErrorText, canSave, primaryButtonArgs } =
  useViewNameValidation(toRef(props, 'serverDuplicateError'));

const onNameInput = () => {
  if (props.serverDuplicateError) emit('clear-server-duplicate-error');
};

const handleClose = () => emit('on-close');

const handleSave = () => {
  if (!canSave.value) return;
  emit('save', trimmedName.value);
};
</script>

<style scoped>
.create-view-modal-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.create-view-modal-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: var(--theme-info-text-neutral);
}

.create-view-modal-info-list {
  margin: 0;
  padding-left: 1.25rem;
}
</style>
